import _ from "lodash";
import hogan, { HoganRenderer } from "hogan.js";
import loggingService from "services/loggingService";
import tags from "extensions/i18n/defaultTags";
import languageList from "strings/languageList.json";
import englishStrings from "locales/en-US";

export type Locale = keyof typeof languageList;

export const compiledTags = _.mapValues(tags, (tagTemplate) =>
    hogan.compile(tagTemplate)
);
// Container for compiled string templates, populated by registerStrings
const compiledStrings = {} as Partial<
    Record<Locale, Partial<Record<LocalizationKey, HoganRenderer>>>
>;

/**
 * Compiles string templates and merges them into an existing collection of
 * compiled templates.
 *
 * @param {Object} uncompiledObject Key value pairs of the form stringId -> uncompiledTemplate.
 * @param {Object} compiledObject Key value pairs of the form stringId -> compiledTemplate. Entries
 *        from uncompiledObject will be merged with this collection.
 */
const compileAndMerge = function (uncompiledObject: any, compiledObject: any) {
    for (const key in uncompiledObject) {
        if (Object.prototype.hasOwnProperty.call(uncompiledObject, key)) {
            compiledObject[key] = hogan.compile(uncompiledObject[key]);
        }
    }
};

export const renderString = (
    locale: Locale,
    i18nKey: LocalizationKey,
    data?: Record<string, unknown>
) => {
    if (!locale) throw new Error("Expected appLocale to have been set");
    const localeTranslations = compiledStrings[locale];
    const englishTranslations = compiledStrings["en-US"]!;
    // The hogan template renderer for the translation, if any
    const renderer =
        localeTranslations?.[i18nKey] ??
        // Fall through to English.
        englishTranslations?.[i18nKey];

    if (!renderer) {
        loggingService.error(i18nKey + " is not localized.");
        return "";
    }

    return renderer.render(data, compiledTags);
};

/**
 * Registers string templates for use within a specific locale.
 *
 * @param strings Key value pairs of stringId -> string template
 * @param locale The locale to register the strings under,
 */
export function registerStrings(
    strings: Record<string, string>,
    locale: Locale
) {
    let compiledLocaleStrings = compiledStrings[locale];

    if (compiledLocaleStrings === undefined) {
        compiledStrings[locale] = compiledLocaleStrings = {};
    }

    compileAndMerge(strings, compiledLocaleStrings);
}

/**
 * Determines if a localization key exists.
 * @param key The localization key to check for.
 */
export function keyExists(name: string, locale: Locale): boolean {
    const localeTranslations = compiledStrings[locale];
    return localeTranslations ? name in localeTranslations : false;
}

export const defaultLocale: Locale = "en-US";
registerStrings(englishStrings, defaultLocale);
export function loadLocale(locale: Locale): Promise<Locale> {
    if (locale === "en-US") {
        return Promise.resolve(locale);
    }
    return import(`locales/${locale}`)
        .then(({ default: strings }) => {
            registerStrings(strings, locale);
            return Promise.resolve(locale);
        })
        .catch(() => {
            loggingService.error("Unable to fetch locale.");
            // fallback to default locale in case of any error loading localized strings.
            return Promise.resolve(defaultLocale);
        });
}
